import { Menu } from 'lucide-react';
import type { ReactNode } from 'react';

import { useTheme } from '../../hooks/useTheme';

import { NAV_THEMES } from './nav-tokens';
import NebariLogoMark from './NebariLogoMark';

interface NebariMobileBarProps {
  drawerOpen: boolean;
  onOpenDrawer: () => void;
  /** Right-aligned. Omitted where the mode has no search. */
  search?: ReactNode;
}

/**
 * The mobile top bar: a hamburger that opens the nav drawer, the BRAND, and a
 * search slot. Hidden from 768px up, where the top bar takes over.
 *
 * The brand, not the page title. This bar used to name the page, which meant
 * every page carrying its own heading printed it twice on a 390px screen — and
 * the pages that did not looked headless once the bar scrolled out of the way.
 * The title belongs to the page; the bar says which product you are in, as the
 * design's MobileChrome has it.
 */
export default function NebariMobileBar({
  drawerOpen,
  onOpenDrawer,
  search,
}: NebariMobileBarProps) {
  const { theme } = useTheme();
  const t = NAV_THEMES[theme];
  return (
    <div
      data-testid="nav-mobile-bar"
      className="flex h-14 shrink-0 items-center gap-3 px-4 navtab:hidden"
      style={{ background: t.bg, borderBottom: `1px solid ${t.border}` }}
    >
      <button
        type="button"
        onClick={onOpenDrawer}
        aria-label="Open navigation"
        aria-expanded={drawerOpen}
        className="flex h-10 w-10 shrink-0 cursor-pointer items-center justify-center rounded-[10px] border-0 bg-transparent"
        style={{ color: t.ink }}
      >
        <Menu size={22} aria-hidden />
      </button>
      <NebariLogoMark size={28} />
      <span className="truncate text-base font-bold -tracking-[0.2px]" style={{ color: t.ink }}>
        Nebari
      </span>
      <div className="flex-1" />
      {search}
    </div>
  );
}
